import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

import { MaybePromise, SingleOrArray } from '@plugins/shared/types';
import { returnAsArray } from '@plugins/shared/utils/arrayUtils/array-utils';

import { MockBrowserLanguageService } from './browser-language.service';
import { MockTranslateExternalOverrideService } from './translate-override.service';
import { AbstractTranslateService } from './translate.service';
import { SourceReturn, Translation, TranslationType } from './types';

@Injectable({ providedIn: 'root' })
export class TestTranslateService extends AbstractTranslateService {
  static override service = new BehaviorSubject<TestTranslateService | null>(
    null,
  );
  constructor() {
    super(
      new MockTranslateExternalOverrideService(),
      new MockBrowserLanguageService(),
    );
    TestTranslateService.service.next(this);
  }
}

type PrepareTestArgs = {
  loaders: SingleOrArray<(lang: string) => SourceReturn>;
  key: string;
  languages?: string[];
  service?: TestTranslateService;
};

/** When supplied with a list of translations and their key,
 * a TestTranslateService is created, the key is registered,
 * and translations are set. */
export const prepareTestTranslateService = async (
  /** Supply a key and associated translations OR supply
   * a hashmap of keys and associated translations */
  { key, languages, loaders, service }: PrepareTestArgs,
): Promise<TestTranslateService> => {
  const testTranslateService = service ?? new TestTranslateService();
  const loaderArray = returnAsArray(loaders);
  const loaderKey = (loader: (lang: string) => SourceReturn) =>
    `${key}${loader.name}`;

  // Each loader should have a unique key
  // to ensure all are loaded before service emits
  // language ready status
  loaderArray.forEach((loader) => {
    testTranslateService.addTranslationLoader({
      key: loaderKey(loader),
      loader,
      type: 'update',
    });
  });
  const defaultLang = testTranslateService.currentLanguage;
  for (const language of languages ?? ['en', 'de']) {
    await lastValueFrom(testTranslateService.use(language));
  }
  testTranslateService.setLanguage(defaultLang);
  return testTranslateService;
};

/** The translations from `targets` test translation services
 * will be included and overriden in the `source` service.
 *
 * The order of the supplied test targets matters, as earlier
 * targets can override translations that come after them.
 *
 * The source service (first argument) is returned. */
export const mergeTranslateTargetsToSource = async (
  sourceService: MaybePromise<TestTranslateService>,
  ...targets: MaybePromise<TestTranslateService>[]
): Promise<TestTranslateService> => {
  const source = await Promise.resolve(sourceService);
  const otherServices = await Promise.all(
    targets.map(async (target) => await Promise.resolve(target)),
  );
  otherServices
    .flatMap((service) => [...service.dependencies])
    .forEach((dep) => {
      source.registerDependency(dep[0], dep[1]);
    });
  const updateValue = otherServices
    .flatMap((service) => {
      return Object.keys(service.translations).length
        ? Object.entries(service.translations)
        : undefined;
    })
    .filter(
      (entry): entry is [string, Translation | undefined] =>
        entry !== undefined,
    )
    .toReversed()
    .reduce<TranslationType>((acc, [language, translations]) => {
      acc[language] = { ...acc[language], ...translations };
      return acc;
    }, {});

  Object.entries(updateValue).forEach(([lang, translations]) => {
    const sourceTranslation = source.getTranslations(lang);
    const updated = { ...translations, ...sourceTranslation };

    source.setTranslations(updated, lang);
  });
  return source;
};
